import request from 'utils/axios';

import { IRecordId } from 'interfaces/general';
import { httpVerbs, LOCATION_MANAGEMENT_BASE_URL, TRIP_TRANSPORT_MANAGEMENT_BASE_URL } from 'configs';
import {
  ICreateTransportRequest,
  IGetTransportsResponse,
  IUpdateTransportRequest,
  ITransportBoardingPass,
  IRemoveTransportBoardingPassResponse,
  IGetCitiesResponse,
  ICalculateDurationRequest,
  ICalculateDurationResponse,
  TransportStationPlaceType,
} from './types';
import { Method } from 'axios';
import { OverviewQueryParams } from '../../types';
import { AddressOutput } from 'utils/types';

const SEGMENT_TRANSPORT_MANAGEMENT = `${TRIP_TRANSPORT_MANAGEMENT_BASE_URL}/api/v1`;
const SEGMENT_LOCATION_MANAGEMENT = `${LOCATION_MANAGEMENT_BASE_URL}/api/v1`;

// Internal API station response includes additional fields like id
export interface InternalStation extends AddressOutput {
  id?: number;
  timezone?: string;
  createdAt?: string;
}

export interface StationSearchResponse {
  success: boolean;
  data: InternalStation[];
  message?: string;
  status?: number;
}

export const searchStations = (
  keyword: string,
  placeType: TransportStationPlaceType,
  cityId?: number,
  cityName?: string,
): Promise<StationSearchResponse> => {
  let url = `${SEGMENT_LOCATION_MANAGEMENT}/station/search?keyword=${encodeURIComponent(keyword)}&placeType=${placeType}`;

  // Add cityId if provided
  if (cityId !== undefined && cityId !== null) {
    url += `&cityId=${cityId}`;
  }

  // Add cityName if provided
  if (cityName) {
    url += `&cityName=${encodeURIComponent(cityName)}`;
  }

  return request({
    url,
    method: httpVerbs.GET as Method,
  });
};

export const addTransportToTrip = (
  data: ICreateTransportRequest,
  agencyId: IRecordId,
  tripId: IRecordId,
): Promise<{ success: boolean; message: string }> => {
  const url = `${SEGMENT_TRANSPORT_MANAGEMENT}/${agencyId}/trips/${tripId}/transports`;
  return request({
    url,
    method: httpVerbs.POST as Method,
    data,
  });
};

export const getTransports = (
  agencyId: IRecordId,
  tripId: IRecordId,
  params?: OverviewQueryParams,
): Promise<IGetTransportsResponse> => {
  const url = `${SEGMENT_TRANSPORT_MANAGEMENT}/${agencyId}/trips/${tripId}/transports`;
  return request({
    url,
    method: httpVerbs.GET as Method,
    params,
  });
};

export const deleteTransport = (
  agencyId: IRecordId,
  tripId: IRecordId,
  transportId: IRecordId,
): Promise<{ success: boolean; message: string }> => {
  const url = `${SEGMENT_TRANSPORT_MANAGEMENT}/${agencyId}/trips/${tripId}/transports/${transportId}`;
  return request({ url, method: httpVerbs.DELETE as Method });
};

export const updateTransport = (
  agencyId: IRecordId,
  tripId: IRecordId,
  transportId: number | undefined,
  data: IUpdateTransportRequest,
): Promise<{ success: boolean; message: string }> => {
  const url = `${SEGMENT_TRANSPORT_MANAGEMENT}/${agencyId}/trips/${tripId}/transports/${transportId}`;
  return request({ url, method: httpVerbs.PUT as Method, data });
};

export const parseTransportBoardingPass = (
  data: FormData,
  agencyId: IRecordId,
  tripId: IRecordId,
  travelerId: number | undefined,
  signal?: AbortSignal,
): Promise<ITransportBoardingPass> => {
  const url = `${SEGMENT_TRANSPORT_MANAGEMENT}/${agencyId}/trips/${tripId}/transports/travelers/${travelerId}/boarding-pass-review`;
  return request({
    url,
    method: httpVerbs.POST as Method,
    data,
    signal,
  });
};

export const removeTransportBoardingPass = (
  agencyId: IRecordId,
  tripId: IRecordId,
  travelerId: IRecordId,
): Promise<IRemoveTransportBoardingPassResponse> => {
  const url = `${SEGMENT_TRANSPORT_MANAGEMENT}/${agencyId}/trips/${tripId}/transports/travelers/${travelerId}/remove-boarding-pass`;
  return request({ url, method: httpVerbs.PUT as Method });
};

export const getCities = (prefix: string): Promise<IGetCitiesResponse> => {
  const url = `${SEGMENT_LOCATION_MANAGEMENT}/cities?prefix=${prefix}`;
  return request({ url, method: httpVerbs.GET as Method });
};

export const calculateDuration = (data: ICalculateDurationRequest): Promise<ICalculateDurationResponse> => {
  const url = `${SEGMENT_LOCATION_MANAGEMENT}/duration/calculate-duration`;
  return request({
    url,
    method: httpVerbs.POST as Method,
    data,
  });
};
